import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import type { QIT } from 'src/components/WSCertificationSummary/types/QuestionnaireSchemaTypes';
import type {
  QuestionnaireAnswer,
  QuestionnaireAnswersMap,
} from 'src/services/RenterQuestionnaire.service';
import {
  useQuestionnaireAutosave,
  type SaveStatus,
} from './hooks/useQuestionnaireAutosave';
import QuestionnaireAnswerInput from './QuestionnaireAnswerInput';
import ConditionalGlow from './ConditionalGlow';

interface QuestionnaireQuestionProps {
  qit: QIT;
  surveyId: number;
  answerGroupId: number | undefined;
  isNewlyRevealed?: boolean;
  revealOrigin?: { questionName: string; choiceName: string } | null;
}

function getTextValue(
  answer: QuestionnaireAnswer | null,
  questionType: string
): string {
  if (!answer) {
    return '';
  }
  if (questionType === 'text_area') {
    return answer.text_area ?? '';
  }
  return answer.text ?? '';
}

function getSelectedChoiceIds(answer: QuestionnaireAnswer | null): number[] {
  if (!answer?.question_choices?.length) {
    return [];
  }
  return answer.question_choices.map((c) => c.id);
}

const SaveStatusIndicator: FC<{ status: SaveStatus }> = ({ status }) => {
  if (status === 'idle') {
    return null;
  }
  const labelMap: Record<SaveStatus, string> = {
    idle: '',
    scheduled: 'Save scheduled',
    saving: 'Saving…',
    saved: 'Saved',
    error: 'Error saving',
  };
  const colorMap: Record<SaveStatus, 'default' | 'info' | 'success' | 'error'> =
    {
      idle: 'default',
      scheduled: 'info',
      saving: 'default',
      saved: 'success',
      error: 'error',
    };
  return (
    <Chip
      label={labelMap[status]}
      color={colorMap[status]}
      size="small"
      variant="outlined"
      sx={{ height: 20, fontSize: '0.7rem' }}
    />
  );
};

const QuestionnaireQuestion: FC<QuestionnaireQuestionProps> = ({
  qit,
  surveyId,
  answerGroupId,
  isNewlyRevealed = false,
  revealOrigin,
}) => {
  const { certificationId } = useParams<{ certificationId: string }>();
  const navigate = useNavigate();
  const certId = certificationId ? Number(certificationId) : NaN;

  const answer = useSelector((state: RootState) => {
    const certAnswers = state.newRenterInterface.answers[certId] as
      | QuestionnaireAnswersMap
      | undefined;
    if (!certAnswers) {
      return null;
    }
    return certAnswers[qit.id]?.[surveyId]?.[answerGroupId ?? 0] ?? null;
  });

  const questionType = qit.question.question_type;
  const choices =
    qit.question_choices.length > 0
      ? qit.question_choices
      : qit.question.question_choices;

  const [localText, setLocalText] = useState(() =>
    getTextValue(answer, questionType)
  );
  const [localChoiceIds, setLocalChoiceIds] = useState(() =>
    getSelectedChoiceIds(answer)
  );
  const lastAnswerRef = useRef(answer);
  const dirtyRef = useRef(false);

  useEffect(() => {
    if (answer !== lastAnswerRef.current) {
      setLocalText(getTextValue(answer, questionType));
      setLocalChoiceIds(getSelectedChoiceIds(answer));
      lastAnswerRef.current = answer;
      dirtyRef.current = false;
    }
  }, [answer, questionType]);

  const { saveStatus, saveError, triggerSave, triggerImmediateSave } =
    useQuestionnaireAutosave({
      surveyId,
      answerId: answer?.id ?? null,
      qitId: qit.id,
      answerGroupId,
    });

  const buildPayload = useCallback(
    (text: string, choiceIds: number[]) => {
      const isChoiceBased = [
        'bool',
        'choices',
        'multiselect',
        'frequency',
      ].includes(questionType);
      if (isChoiceBased) {
        return { question_choices: choiceIds };
      }
      if (questionType === 'text_area') {
        return { text_area: text };
      }
      return { text };
    },
    [questionType]
  );

  const handleChange = useCallback(
    (value: string, choiceIds?: number[]) => {
      dirtyRef.current = true;
      setLocalText(value);
      if (choiceIds !== undefined) {
        setLocalChoiceIds(choiceIds);
      }
      const nextChoices = choiceIds ?? localChoiceIds;
      triggerSave(buildPayload(value, nextChoices));
    },
    [triggerSave, buildPayload, localChoiceIds]
  );

  const handleBlur = useCallback(() => {
    if (!dirtyRef.current) {
      return;
    }
    triggerImmediateSave(buildPayload(localText, localChoiceIds));
  }, [triggerImmediateSave, buildPayload, localText, localChoiceIds]);

  const handleFileUpload = useCallback(() => {
    navigate(`/applicant/upload/${surveyId}/${answerGroupId ?? 0}`);
  }, [navigate, surveyId, answerGroupId]);

  const isRequired = qit.necessity === 1;
  const fileCount = answer?.files?.length ?? 0;

  return (
    <ConditionalGlow isNewlyRevealed={isNewlyRevealed} origin={revealOrigin}>
      <Box sx={{ py: 1.5, px: 2.5 }}>
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
          <Typography variant="body2" fontWeight={500}>
            {qit.question.name}
            {isRequired && (
              <Typography component="span" color="error.main" sx={{ ml: 0.5 }}>
                *
              </Typography>
            )}
          </Typography>
          <SaveStatusIndicator status={saveStatus} />
        </Stack>

        {questionType === 'file' ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<UploadFileIcon />}
            onClick={handleFileUpload}
          >
            <span>
              {fileCount > 0
                ? `${fileCount} file${fileCount !== 1 ? 's' : ''} uploaded`
                : 'Upload files'}
            </span>
          </Button>
        ) : (
          <QuestionnaireAnswerInput
            questionType={questionType}
            questionLabel={qit.question.name}
            value={localText}
            choices={choices}
            selectedChoiceIds={localChoiceIds}
            onChange={handleChange}
            onBlur={handleBlur}
            error={saveError}
            helpText={qit.help_text || undefined}
          />
        )}
      </Box>
    </ConditionalGlow>
  );
};

export default QuestionnaireQuestion;
